import { useCallback, useEffect, useRef, useState } from "react";

// Dirty / saving / autosave bookkeeping for an editor page.
//
// Edits are counted in a ref, not state, so a keystroke never re-renders
// the page — only the clean→dirty transition does. Every edit restarts the
// autosave timer (debounce on inactivity); a save never overlaps another,
// and edits that land while a save is in flight keep the document dirty
// and re-arm the timer once it finishes.

const DEFAULT_AUTOSAVE_MS = 30_000;

export type SaveStatus = "clean" | "dirty" | "saving" | "failed";

interface SaveTrackerOptions {
    /** Fired after `delayMs` without edits while there are unsaved changes. */
    onAutosave: () => void;
    /** Surfaces a failed save (manual or automatic). */
    onError: (message: string) => void;
    delayMs?: number;
}

export interface SaveTracker {
    status: SaveStatus;
    /** Call from the editor's change events. */
    markDirty: () => void;
    /** Call after a document is loaded / created. */
    markClean: () => void;
    /** While on, change events are ignored (e.g. the edits a load itself makes). */
    setSuppressed: (on: boolean) => void;
    /** Runs one save; resolves false when skipped (another in flight) or failed. */
    runSave: (save: () => Promise<void>) => Promise<boolean>;
    hasUnsavedChanges: () => boolean;
    /** Resolves once no save is in flight (immediately when idle). */
    whenSaved: () => Promise<void>;
}

export function useSaveTracker({
    onAutosave, onError, delayMs = DEFAULT_AUTOSAVE_MS
}: SaveTrackerOptions): SaveTracker {
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);
    const dirtyRef = useRef(false);
    const savingRef = useRef(false);
    const suppressedRef = useRef(false);
    const editSeqRef = useRef(0);
    const timerRef = useRef<number | null>(null);
    // Settles when the save in flight finishes — lets Close wait for it.
    const inFlightRef = useRef<Promise<void> | null>(null);
    const onAutosaveRef = useRef(onAutosave);
    const onErrorRef = useRef(onError);
    onAutosaveRef.current = onAutosave;
    onErrorRef.current = onError;

    const cancelAutosave = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const scheduleAutosave = useCallback(() => {
        cancelAutosave();
        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            if (dirtyRef.current && !savingRef.current) onAutosaveRef.current();
        }, delayMs);
    }, [cancelAutosave, delayMs]);

    const setDirtyFlag = useCallback((next: boolean) => {
        dirtyRef.current = next;
        setDirty(next);
    }, []);

    const markDirty = useCallback(() => {
        if (suppressedRef.current) return;
        editSeqRef.current += 1;
        if (!dirtyRef.current) setDirtyFlag(true);
        scheduleAutosave();
    }, [scheduleAutosave, setDirtyFlag]);

    const markClean = useCallback(() => {
        cancelAutosave();
        // Bumping the sequence stops a save still in flight for the previous
        // document from marking anything clean when it completes.
        editSeqRef.current += 1;
        setDirtyFlag(false);
        setFailed(false);
    }, [cancelAutosave, setDirtyFlag]);

    const setSuppressed = useCallback((on: boolean) => {
        suppressedRef.current = on;
    }, []);

    const runSave = useCallback(async (save: () => Promise<void>): Promise<boolean> => {
        if (savingRef.current) return false;
        savingRef.current = true;
        setSaving(true);
        cancelAutosave();
        let settle = () => {};
        inFlightRef.current = new Promise<void>((resolve) => { settle = resolve; });
        const startSeq = editSeqRef.current;
        try {
            await save();
            setFailed(false);
            if (editSeqRef.current === startSeq) setDirtyFlag(false);
            else if (dirtyRef.current) scheduleAutosave();
            return true;
        } catch (err) {
            setFailed(true);
            onErrorRef.current(err instanceof Error ? err.message : String(err));
            return false;
        } finally {
            savingRef.current = false;
            setSaving(false);
            inFlightRef.current = null;
            settle();
        }
    }, [cancelAutosave, scheduleAutosave, setDirtyFlag]);

    const hasUnsavedChanges = useCallback(() => dirtyRef.current || savingRef.current, []);
    const whenSaved = useCallback(() => inFlightRef.current ?? Promise.resolve(), []);

    useEffect(() => cancelAutosave, [cancelAutosave]);

    // Browser-level guard for closing / reloading the tab.
    useEffect(() => {
        if (!dirty && !saving) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty, saving]);

    const status: SaveStatus = saving ? "saving" : failed ? "failed" : dirty ? "dirty" : "clean";
    return { status, markDirty, markClean, setSuppressed, runSave, hasUnsavedChanges, whenSaved };
}
